import { useEffect, useState } from 'react';
import {
  Grid,
  Column,
  Heading,
  InlineNotification,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  SkeletonText,
  Tag,
} from '@carbon/react';
import {
  Ai,
  DocumentMultiple_01,
  DataStructured,
  ChartLine,
  type CarbonIconType,
} from '@carbon/icons-react';
import { meUsageApi, type MyUsageStats } from '@/lib/api/usage.api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const OPERATION_LABELS: Record<string, string> = {
  generate_ontology: 'Ontology generation',
  generate_from_documents: 'Generation from documents',
  extend_from_documents: 'Extension from documents',
  expand_concept: 'Concept expansion',
  embedding: 'Embeddings',
  chat: 'Chat',
};

// ─── Stat tile ────────────────────────────────────────────────────────────────

interface StatTileProps {
  label: string;
  value: number;
  sublabel?: string;
  icon: CarbonIconType;
  loading: boolean;
}

function StatTile({ label, value, sublabel, icon: Icon, loading }: StatTileProps) {
  return (
    <div
      style={{
        background: 'var(--cds-layer)',
        padding: '1.25rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: 'var(--cds-text-secondary)',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        <Icon size={14} />
        {label}
      </div>
      {loading ? (
        <SkeletonText width="60%" />
      ) : (
        <>
          <div style={{ fontSize: '2rem', fontWeight: 300, lineHeight: 1 }}>
            {formatNumber(value)}
          </div>
          {sublabel && (
            <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
              {sublabel}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tile grid wrapper ────────────────────────────────────────────────────────

function TileGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1px',
        background: 'var(--cds-border-subtle)',
        border: '1px solid var(--cds-border-subtle)',
        marginBottom: '2rem',
      }}
    >
      {children}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  description,
}: {
  icon: CarbonIconType;
  title: string;
  description?: string;
}) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <h2
        style={{
          fontSize: '1rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: description ? '0.25rem' : 0,
        }}
      >
        <Icon size={18} />
        {title}
      </h2>
      {description && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--cds-text-secondary)', maxWidth: 640 }}>
          {description}
        </p>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UsagePage() {
  const [stats, setStats] = useState<MyUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadStats();
  }, []);

  async function loadStats() {
    setLoading(true);
    setError(null);
    try {
      const data = await meUsageApi.getUsage();
      setStats(data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="usage-page" style={{ padding: '2rem 0' }}>
      <Grid fullWidth>
        {/* ── Page header ──────────────────────────────────────────────── */}
        <Column lg={16} md={8} sm={4}>
          <Heading>Usage</Heading>
          <p
            style={{
              color: 'var(--cds-text-secondary)',
              fontSize: '0.875rem',
              marginTop: '0.5rem',
              marginBottom: '2rem',
            }}
          >
            Your lifetime consumption of AI tokens, documents, and platform resources.{' '}
            <Tag type="warm-gray" size="sm" style={{ verticalAlign: 'middle' }}>
              Estimated
            </Tag>{' '}
            — token counts for some operations are approximated from character counts.
          </p>
        </Column>

        {/* ── Error ────────────────────────────────────────────────────── */}
        {error && (
          <Column lg={16} md={8} sm={4} style={{ marginBottom: '1rem' }}>
            <InlineNotification
              kind="error"
              lowContrast
              title="Error loading usage"
              subtitle={error}
              onClose={() => setError(null)}
            />
          </Column>
        )}

        {/* ── Token overview ───────────────────────────────────────────── */}
        <Column lg={16} md={8} sm={4}>
          <SectionHeader
            icon={Ai}
            title="AI Token Consumption"
            description="Tokens used by all AI operations since your account was created."
          />
          <TileGrid>
            <StatTile
              label="Total tokens"
              value={stats?.total_tokens ?? 0}
              sublabel="prompt + completion"
              icon={Ai}
              loading={loading}
            />
            <StatTile
              label="Prompt tokens"
              value={stats?.prompt_tokens ?? 0}
              icon={Ai}
              loading={loading}
            />
            <StatTile
              label="Completion tokens"
              value={stats?.completion_tokens ?? 0}
              icon={Ai}
              loading={loading}
            />
            <StatTile
              label="AI calls"
              value={stats?.event_count ?? 0}
              sublabel={
                stats?.last_token_use_at
                  ? `Last: ${new Date(stats.last_token_use_at).toLocaleDateString()}`
                  : undefined
              }
              icon={ChartLine}
              loading={loading}
            />
          </TileGrid>
        </Column>

        {/* ── Per-operation breakdown ───────────────────────────────────── */}
        {(loading || (stats && stats.by_operation.length > 0)) && (
          <Column lg={16} md={8} sm={4}>
            <SectionHeader icon={ChartLine} title="Breakdown by operation" />
            {loading ? (
              <div style={{ marginBottom: '2rem' }}>
                <SkeletonText paragraph lineCount={4} />
              </div>
            ) : (
              <div style={{ marginBottom: '2rem' }}>
                <StructuredListWrapper style={{ maxWidth: 760 }}>
                  <StructuredListHead>
                    <StructuredListRow head>
                      <StructuredListCell head>Operation</StructuredListCell>
                      <StructuredListCell head style={{ textAlign: 'right' }}>
                        Total tokens
                      </StructuredListCell>
                      <StructuredListCell head style={{ textAlign: 'right' }}>
                        Prompt
                      </StructuredListCell>
                      <StructuredListCell head style={{ textAlign: 'right' }}>
                        Completion
                      </StructuredListCell>
                      <StructuredListCell head style={{ textAlign: 'right' }}>
                        Calls
                      </StructuredListCell>
                    </StructuredListRow>
                  </StructuredListHead>
                  <StructuredListBody>
                    {stats!.by_operation.map((row) => (
                      <StructuredListRow key={row.operation}>
                        <StructuredListCell>
                          {OPERATION_LABELS[row.operation] ?? row.operation}
                        </StructuredListCell>
                        <StructuredListCell
                          style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {formatNumber(row.total_tokens)}
                        </StructuredListCell>
                        <StructuredListCell
                          style={{
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--cds-text-secondary)',
                          }}
                        >
                          {formatNumber(row.prompt_tokens)}
                        </StructuredListCell>
                        <StructuredListCell
                          style={{
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--cds-text-secondary)',
                          }}
                        >
                          {formatNumber(row.completion_tokens)}
                        </StructuredListCell>
                        <StructuredListCell
                          style={{
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--cds-text-secondary)',
                          }}
                        >
                          {row.event_count.toLocaleString()}
                        </StructuredListCell>
                      </StructuredListRow>
                    ))}
                  </StructuredListBody>
                </StructuredListWrapper>
              </div>
            )}
          </Column>
        )}

        {/* ── Document processing ───────────────────────────────────────── */}
        <Column lg={16} md={8} sm={4}>
          <SectionHeader
            icon={DocumentMultiple_01}
            title="Document Processing"
            description="Documents converted by the Docling pipeline (OCR + text extraction)."
          />
          <TileGrid>
            <StatTile
              label="Documents converted"
              value={stats?.docling_conversions ?? 0}
              icon={DocumentMultiple_01}
              loading={loading}
            />
            <StatTile
              label="Pages processed"
              value={stats?.docling_pages ?? 0}
              sublabel="total pages across all documents"
              icon={DocumentMultiple_01}
              loading={loading}
            />
          </TileGrid>
        </Column>

        {/* ── Content summary ───────────────────────────────────────────── */}
        <Column lg={16} md={8} sm={4}>
          <SectionHeader
            icon={DataStructured}
            title="Content Summary"
            description="All ontologies, knowledge bases, and knowledge units you have created."
          />
          <TileGrid>
            <StatTile
              label="Ontologies"
              value={stats?.ontology_count ?? 0}
              icon={DataStructured}
              loading={loading}
            />
            <StatTile
              label="Ontology nodes"
              value={stats?.node_count ?? 0}
              icon={DataStructured}
              loading={loading}
            />
            <StatTile
              label="Ontology edges"
              value={stats?.edge_count ?? 0}
              icon={DataStructured}
              loading={loading}
            />
            <StatTile
              label="Knowledge bases"
              value={stats?.kb_count ?? 0}
              icon={DocumentMultiple_01}
              loading={loading}
            />
            <StatTile
              label="Documents"
              value={stats?.ko_count ?? 0}
              sublabel="knowledge objects"
              icon={DocumentMultiple_01}
              loading={loading}
            />
            <StatTile
              label="Knowledge units"
              value={stats?.ku_count ?? 0}
              sublabel="extracted chunks"
              icon={DocumentMultiple_01}
              loading={loading}
            />
          </TileGrid>
        </Column>
      </Grid>
    </div>
  );
}

// Made with Bob
